"use client"

import { useEffect, useMemo, useState, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import { X, User, Phone, Mail, GraduationCap, MapPin, MessageSquare, AlertCircle, Save, Users, Baby, BookOpen, Bot, CheckCircle2, XCircle, RefreshCw, WhatsappLogo, Plus, Trash2 } from "@/lib/icons"

import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { LEAD_STAGE_META, normalizeLeadStage } from "@/lib/lead-stages"
import { checkWhatsAppNumber } from "@/lib/n8n-api"
import { cn } from "@/lib/utils"
import { NIVEL_OPTIONS } from "@/lib/educational-levels"

const PRIORIDAD_OPTIONS = [
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baja", label: "Baja" },
]

const CALIDAD_OPTIONS = [
  { value: "frio", label: "Frio" },
  { value: "tibio", label: "Tibio" },
  { value: "caliente", label: "Caliente" },
  { value: "spam", label: "Spam" },
]

const ESTADO_LEAD_OPTIONS = [
  { value: "nuevo", label: "Nuevo" },
  { value: "contactado", label: "Contactado" },
  { value: "cita_solicitada", label: "Cita solicitada" },
  { value: "cita", label: "Cita" },
  { value: "interesado", label: "Entrevista / Examen" },
  { value: "entrega_resultado", label: "Entrega de Resultado" },
  { value: "pendiente_pago", label: "Pendiente de Pago" },
  { value: "inscrito", label: "Inscrito" },
  { value: "perdido", label: "Perdido" },
  { value: "spam", label: "Spam" },
]

const PREFERENCIA_OPTIONS = [
  { value: "llamada", label: "Llamada" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
]

const ESTADOS_MX = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
  "Chiapas", "Chihuahua", "Coahuila", "Colima", "Durango", "Guanajuato",
  "Guerrero", "Hidalgo", "Jalisco", "Estado de Mexico", "Michoacan",
  "Morelos", "Nayarit", "Nuevo Leon", "Oaxaca", "Puebla", "Queretaro",
  "Quintana Roo", "San Luis Potosi", "Sinaloa", "Sonora", "Tabasco",
  "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatan", "Zacatecas",
]

const EMPTY_HIJO = { nombre: "", nivel_educativo: "", grado_especifico: "", edad: "", sexo: "" }

const INITIAL_FORM = {
  nombre_tutor: "",
  nombre_alumno: "",
  telefono: "",
  email: "",
  nivel_educativo: "",
  grado_especifico: "",
  es_familia: false,
  num_hijos: 1,
  estado: "",
  municipio: "",
  preferencia_contacto: "",
  prioridad: "",
  calidad_lead: "tibio",
  estado_lead: "nuevo",
  tema_consulta: "",
  notas: "",
  hijos: [{ ...EMPTY_HIJO }], // siempre arranca con 1 hijo vacío
}

const SEXO_OPTIONS = [
  { value: "", label: "Sexo..." },
  { value: "nino", label: "Niño" },
  { value: "nina", label: "Niña" },
]

// Shared select component
function Select({ value, onChange, options, placeholder, className }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={cn(
        "h-9 w-full rounded-lg border border-border bg-background px-3 text-xs transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring",
        className
      )}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

// Field wrapper
function Field({ label, icon: Icon, children, className }) {
  return (
    <div className={cn("space-y-1", className)}>
      <Label className="text-[11px] font-medium text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </Label>
      {children}
    </div>
  )
}

export function LeadEditorModal({ lead, open, onClose, onSave, mode = "edit" }) {
  const [formData, setFormData] = useState(INITIAL_FORM)
  const [saving, setSaving] = useState(false)
  const isCreate = mode === "create"

  const [waStatus, setWaStatus] = useState(null)
  const lastCheckedPhone = useRef("")

  const isOpen = Boolean(open && (lead || isCreate))

  useEffect(() => {
    if (!isOpen) return
    if (isCreate) {
      setFormData({ ...INITIAL_FORM })
      setWaStatus(null)
      lastCheckedPhone.current = ""
    } else {
      setFormData({
        ...INITIAL_FORM,
        ...lead,
        telefono: lead?.telefono || "",
        email: lead?.email || "",
        nombre_alumno: lead?.nombre_alumno || "",
        grado_especifico: lead?.grado_especifico || "",
        es_familia: lead?.es_familia ?? false,
        num_hijos: lead?.num_hijos ?? 1,
        estado_lead: lead?.estado_lead || "nuevo",
        notas: lead?.notas || "",
      })
    }
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = "" }
  }, [lead, isOpen, isCreate])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => { if (e.key === "Escape" && !saving) onClose() }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose, saving])

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }))
    if (key === "telefono") setWaStatus(null)
  }

  const handleCheckWhatsApp = useCallback(async () => {
    const phone = formData.telefono?.replace(/\D/g, "")
    if (!phone || phone.length < 10 || phone === lastCheckedPhone.current) return
    lastCheckedPhone.current = phone
    setWaStatus("checking")
    const result = await checkWhatsAppNumber(phone)
    if (result.error) setWaStatus("error")
    else setWaStatus(result.exists ? "valid" : "invalid")
  }, [formData.telefono])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isCreate && !lead) return
    if (isCreate && !formData.nombre_tutor?.trim()) return
    setSaving(true)
    if (isCreate) await onSave(formData)
    else await onSave({ ...lead, ...formData })
    setSaving(false)
  }

  const infoResumen = useMemo(() => {
    if (!lead) return []
    const stage = normalizeLeadStage(lead.estado_lead)
    const stageTitle = LEAD_STAGE_META[stage]?.title || lead.estado_lead
    return [
      { label: "Estado actual", value: stageTitle },
      { label: "Origen", value: lead.origen || "—" },
      { label: "Campus", value: lead.campus || lead.campus_id || "—" },
      { label: "Ultima actividad", value: lead.updated_at ? new Date(lead.updated_at).toLocaleString() : "—" },
      { label: "Datos completos", value: lead.datos_completos ? "Si" : "No" },
      { label: "Tema consulta", value: lead.tema_consulta || "—" },
    ]
  }, [lead])

  if (!isOpen) return null

  // ─── CREATE MODE: Clean, minimal form ───
  if (isCreate) {
    return createPortal(
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm sm:p-4"
        role="dialog"
        aria-modal="true"
        onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose() }}
      >
        <form
          className="relative w-full sm:max-w-lg max-h-[92vh] overflow-hidden flex flex-col rounded-t-2xl sm:rounded-2xl bg-background shadow-2xl border border-border animate-in slide-in-from-bottom sm:zoom-in-95 sm:slide-in-from-bottom-0 duration-200"
          onSubmit={handleSubmit}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center gap-3 px-5 py-4 border-b border-border">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <User className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-sm font-bold">Nuevo prospecto</h2>
              <p className="text-[11px] text-muted-foreground">Captura tutor + hijos en un solo paso</p>
            </div>
            <button type="button" onClick={onClose} disabled={saving} className="rounded-lg p-1.5 hover:bg-muted transition-colors">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* Form body — scrollable */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Nombre tutor */}
            <Field label="Nombre del tutor *" icon={User}>
              <Input
                value={formData.nombre_tutor}
                onChange={(e) => handleChange("nombre_tutor", e.target.value)}
                placeholder="Nombre completo"
                className="h-9 text-sm"
                autoFocus
              />
            </Field>

            {/* Telefono + Email en una fila */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Teléfono *" icon={Phone}>
                <div className="relative">
                  <Input
                    value={formData.telefono}
                    onChange={(e) => handleChange("telefono", e.target.value)}
                    onBlur={handleCheckWhatsApp}
                    placeholder="10 dígitos"
                    className={cn(
                      "h-9 text-sm pr-20",
                      waStatus === "valid" && "border-emerald-400 focus:ring-emerald-300",
                      waStatus === "invalid" && "border-red-400 focus:ring-red-300",
                    )}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    {waStatus === "checking" && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />}
                    {waStatus === "valid" && (
                      <span className="flex items-center gap-0.5 text-[9px] font-semibold text-emerald-600">
                        <WhatsappLogo className="h-3 w-3" /> WA
                      </span>
                    )}
                    {waStatus === "invalid" && (
                      <span className="flex items-center gap-0.5 text-[9px] font-semibold text-red-500">
                        <XCircle className="h-3 w-3" /> No
                      </span>
                    )}
                  </div>
                </div>
              </Field>

              <Field label="Email (opcional)" icon={Mail}>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="h-9 text-sm"
                />
              </Field>
            </div>

            {/* Tema de consulta */}
            <Field label="Tema de consulta (opcional)" icon={AlertCircle}>
              <Input
                value={formData.tema_consulta}
                onChange={(e) => handleChange("tema_consulta", e.target.value)}
                placeholder="Ej: admisión, costos, becas..."
                className="h-9 text-sm"
              />
            </Field>

            {/* ─── Bloque de hijos ─── */}
            <div className="rounded-xl border border-rose-200/70 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-950/20 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
                  <Baby className="h-3 w-3" /> Hijos del prospecto
                </Label>
                <span className="text-[10px] font-semibold text-rose-600 dark:text-rose-400 bg-white dark:bg-slate-900 px-2 py-0.5 rounded-full border border-rose-200 dark:border-rose-900/50">
                  {formData.hijos.length} {formData.hijos.length === 1 ? "hijo" : "hijos"}
                </span>
              </div>

              {formData.hijos.map((hijo, idx) => (
                <div
                  key={idx}
                  className="rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2.5 space-y-2 relative group"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 text-[10px] font-bold flex-shrink-0">
                      {idx + 1}
                    </span>
                    <Input
                      value={hijo.nombre}
                      onChange={(e) => {
                        const next = [...formData.hijos]
                        next[idx] = { ...next[idx], nombre: e.target.value }
                        handleChange("hijos", next)
                      }}
                      placeholder={idx === 0 ? "Nombre del primer hijo *" : `Nombre del hijo ${idx + 1}`}
                      className="h-8 text-xs flex-1"
                    />
                    {formData.hijos.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = formData.hijos.filter((_, i) => i !== idx)
                          handleChange("hijos", next)
                        }}
                        className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        title="Eliminar hijo"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-1.5">
                    <Select
                      value={hijo.nivel_educativo}
                      onChange={(e) => {
                        const next = [...formData.hijos]
                        next[idx] = { ...next[idx], nivel_educativo: e.target.value }
                        handleChange("hijos", next)
                      }}
                      options={NIVEL_OPTIONS}
                      placeholder="Nivel..."
                      className="h-8 text-[11px]"
                    />
                    <Select
                      value={hijo.sexo}
                      onChange={(e) => {
                        const next = [...formData.hijos]
                        next[idx] = { ...next[idx], sexo: e.target.value }
                        handleChange("hijos", next)
                      }}
                      options={SEXO_OPTIONS}
                      className="h-8 text-[11px]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-1.5">
                    <Input
                      value={hijo.grado_especifico}
                      onChange={(e) => {
                        const next = [...formData.hijos]
                        next[idx] = { ...next[idx], grado_especifico: e.target.value }
                        handleChange("hijos", next)
                      }}
                      placeholder="Grado (Ej: 3ro)"
                      className="h-8 text-[11px]"
                    />
                    <Input
                      type="number"
                      value={hijo.edad}
                      onChange={(e) => {
                        const next = [...formData.hijos]
                        next[idx] = { ...next[idx], edad: e.target.value }
                        handleChange("hijos", next)
                      }}
                      placeholder="Edad"
                      min="0"
                      max="25"
                      className="h-8 text-[11px]"
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => handleChange("hijos", [...formData.hijos, { ...EMPTY_HIJO }])}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-[11px] font-semibold hover:bg-rose-100 dark:hover:bg-rose-950/30 transition-colors"
              >
                <Plus className="h-3 w-3" /> Agregar otro hijo
              </button>
            </div>

            {/* Notas */}
            <Field label="Notas (opcional)" icon={MessageSquare}>
              <Textarea
                value={formData.notas}
                onChange={(e) => handleChange("notas", e.target.value)}
                placeholder="Alguna observación inicial..."
                className="min-h-[50px] text-sm resize-none"
              />
            </Field>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 flex items-center gap-2 px-5 py-3 border-t border-border bg-muted/30">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving} className="flex-1 h-9 text-xs">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving || !formData.nombre_tutor?.trim() || !formData.telefono?.trim()}
              className="flex-1 h-9 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saving ? "Creando..." : "Crear prospecto"}
            </Button>
          </div>
        </form>
      </div>,
      document.body
    )
  }

  // ─── EDIT MODE ───
  const stageName = normalizeLeadStage(lead?.estado_lead)
  const stageMeta = LEAD_STAGE_META[stageName] || {}
  const StageIcon = stageMeta.icon || User
  const leadName = lead?.nombre_tutor || "Lead sin nombre"
  const initials = leadName.split(" ").map(p => p.charAt(0)).join("").slice(0, 2).toUpperCase() || "LD"

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose() }}
    >
      <form
        className="relative w-full sm:max-w-2xl h-[90vh] sm:h-auto sm:max-h-[82vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-background shadow-2xl border border-border animate-in slide-in-from-bottom sm:zoom-in-95 sm:slide-in-from-bottom-0 duration-200"
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile drag indicator */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-muted-foreground/20 rounded-full" />
        </div>

        {/* Header */}
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border px-4 py-3 bg-background/95 backdrop-blur-sm">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-bold truncate">{leadName}</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  <StageIcon className="h-3 w-3" />
                  {stageMeta.title || lead?.estado_lead || "Sin estado"}
                </span>
                {lead?.telefono && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Phone className="h-2.5 w-2.5" /> {lead.telefono}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg p-1.5 hover:bg-muted transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </header>

        <section className="p-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            {/* Resumen */}
            <div className="rounded-xl border border-border bg-card order-2 lg:order-1">
              <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
                <User className="h-3.5 w-3.5 text-primary" />
                <p className="text-xs font-semibold">Resumen</p>
              </div>
              <div className="grid grid-cols-2 gap-1.5 p-2.5">
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><User className="h-2.5 w-2.5" /> Tutor</p>
                  <p className="mt-0.5 text-xs font-semibold truncate">{lead.nombre_tutor || lead.nombre || "—"}</p>
                </div>
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><Baby className="h-2.5 w-2.5" /> Alumno</p>
                  <p className="mt-0.5 text-xs font-semibold truncate">{lead.nombre_alumno || "—"}</p>
                </div>
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><GraduationCap className="h-2.5 w-2.5" /> Nivel</p>
                  <p className="mt-0.5 text-xs font-semibold truncate">
                    {NIVEL_OPTIONS.find(o => o.value === (lead.nivel_educativo || "").toLowerCase())?.label || lead.nivel_educativo || "—"}
                    {lead.grado_especifico ? ` · ${lead.grado_especifico}` : ""}
                  </p>
                </div>
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"><Phone className="h-2.5 w-2.5" /> Tel</p>
                  <p className="mt-0.5 text-xs font-semibold truncate">{lead.telefono || "—"}</p>
                </div>
              </div>
              <dl className="divide-y divide-border max-h-[140px] overflow-y-auto text-xs">
                {infoResumen.map(row => (
                  <div key={row.label} className="flex justify-between gap-2 px-3 py-1.5">
                    <dt className="text-muted-foreground flex-shrink-0">{row.label}</dt>
                    <dd className="truncate text-right font-medium">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Edit form */}
            <div className="rounded-xl border border-border bg-card order-1 lg:order-2">
              <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
                <MessageSquare className="h-3.5 w-3.5 text-emerald-500" />
                <p className="text-xs font-semibold">Editar datos</p>
              </div>
              <div className="p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Nombre del tutor" className="col-span-2 sm:col-span-1">
                    <Input value={formData.nombre_tutor} onChange={(e) => handleChange("nombre_tutor", e.target.value)} className="h-8 text-xs" />
                  </Field>
                  <Field label="Nombre del alumno" className="col-span-2 sm:col-span-1">
                    <Input value={formData.nombre_alumno} onChange={(e) => handleChange("nombre_alumno", e.target.value)} className="h-8 text-xs" placeholder="Extraido por IA" />
                  </Field>

                  <Field label="Telefono">
                    <div className="relative">
                      <Input
                        value={formData.telefono}
                        onChange={(e) => handleChange("telefono", e.target.value)}
                        onBlur={handleCheckWhatsApp}
                        className={cn(
                          "h-8 text-xs pr-20",
                          waStatus === "valid" && "border-emerald-400",
                          waStatus === "invalid" && "border-red-400",
                        )}
                        placeholder="10 digitos"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        {waStatus === "checking" && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />}
                        {waStatus === "valid" && (
                          <span className="flex items-center gap-0.5 text-[9px] font-bold text-emerald-600">
                            <WhatsappLogo className="h-3 w-3" /> WA
                          </span>
                        )}
                        {waStatus === "invalid" && (
                          <span className="flex items-center gap-0.5 text-[9px] font-bold text-red-500">
                            <XCircle className="h-3 w-3" /> No
                          </span>
                        )}
                      </div>
                    </div>
                  </Field>
                  <Field label="Correo">
                    <Input type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} className="h-8 text-xs" />
                  </Field>

                  <Field label="Nivel">
                    <Select
                      value={(formData.nivel_educativo || "").toLowerCase()}
                      onChange={(e) => handleChange("nivel_educativo", e.target.value)}
                      options={NIVEL_OPTIONS}
                      placeholder="Selecciona"
                      className="h-8"
                    />
                  </Field>
                  <Field label="Grado especifico">
                    <Input value={formData.grado_especifico} onChange={(e) => handleChange("grado_especifico", e.target.value)} className="h-8 text-xs" placeholder="Ej: K2, 3ro" />
                  </Field>

                  <Field label="Estado lead">
                    <Select value={formData.estado_lead} onChange={(e) => handleChange("estado_lead", e.target.value)} options={ESTADO_LEAD_OPTIONS} className="h-8" />
                  </Field>
                  <Field label="Calidad">
                    <Select value={formData.calidad_lead} onChange={(e) => handleChange("calidad_lead", e.target.value)} options={CALIDAD_OPTIONS} className="h-8" />
                  </Field>

                  {/* Familia toggle */}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between h-8 rounded-lg border border-border bg-muted/30 px-3">
                      <Label className="text-[11px] flex items-center gap-1.5 cursor-pointer text-muted-foreground" htmlFor="es_familia">
                        <Users className="h-3 w-3" /> Es familia (varios hijos)
                      </Label>
                      <button
                        id="es_familia"
                        type="button"
                        onClick={() => handleChange("es_familia", !formData.es_familia)}
                        className={cn("h-5 w-9 rounded-full flex items-center transition-colors", formData.es_familia ? "bg-primary" : "bg-muted-foreground/30")}
                      >
                        <span className={cn("h-3.5 w-3.5 bg-white rounded-full shadow transition-transform", formData.es_familia ? "translate-x-4" : "translate-x-0.5")} />
                      </button>
                    </div>
                  </div>
                  {formData.es_familia && (
                    <Field label="Numero de hijos" className="col-span-2 sm:col-span-1">
                      <Input type="number" min={1} max={10} value={formData.num_hijos} onChange={(e) => handleChange("num_hijos", parseInt(e.target.value) || 1)} className="h-8 text-xs" />
                    </Field>
                  )}

                  <Field label="Estado">
                    <Select
                      value={formData.estado}
                      onChange={(e) => handleChange("estado", e.target.value)}
                      options={ESTADOS_MX.map(e => ({ value: e, label: e }))}
                      placeholder="Selecciona"
                      className="h-8"
                    />
                  </Field>
                  <Field label="Municipio">
                    <Input value={formData.municipio} onChange={(e) => handleChange("municipio", e.target.value)} className="h-8 text-xs" />
                  </Field>

                  <Field label="Preferencia contacto">
                    <Select value={formData.preferencia_contacto} onChange={(e) => handleChange("preferencia_contacto", e.target.value)} options={PREFERENCIA_OPTIONS} placeholder="Selecciona" className="h-8" />
                  </Field>
                  <Field label="Prioridad">
                    <Select value={formData.prioridad} onChange={(e) => handleChange("prioridad", e.target.value)} options={PRIORIDAD_OPTIONS} placeholder="Selecciona" className="h-8" />
                  </Field>
                </div>
              </div>
            </div>
          </div>

          {/* Notas */}
          <div className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
              <p className="text-xs font-semibold">Notas</p>
            </div>
            <Textarea
              value={formData.notas}
              onChange={(e) => handleChange("notas", e.target.value)}
              placeholder="Hallazgos o acuerdos..."
              className="min-h-[60px] text-xs resize-none"
            />
          </div>
        </section>

        {/* Footer */}
        <footer className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border bg-background/95 backdrop-blur-sm px-4 py-2.5">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving} className="h-8 text-xs px-4">
            Cancelar
          </Button>
          <Button type="submit" disabled={saving} className="h-8 text-xs gap-1.5 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">
            <Save className="h-3.5 w-3.5" />
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </footer>
      </form>
    </div>,
    document.body
  )
}
